import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container, Card, CardBody, Row, Col,
  Table, Badge, Button, Input, InputGroup, Spinner,
  UncontrolledTooltip, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { Plus, Search, Star, Edit2, Eye, Trash2, RefreshCw } from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import * as formService from "../../../services/formService";
import { getDepartments, getScopes } from "../../../services/sahaInspectionService";
import { listTemplates } from "../../../services/freeFormService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

// ─────────────────────────────────────────────────────────────────────────────
// Form Listesi — şablon envanteri.
//
// İki kaynak tek tabloda:
//   1) Denetim formu şablonları  → inspection/types (her birimin, her kapsamın
//      altındaki tüm tipler). Seviye (Denetim / Uygunsuzluk / DÖF) BURADA
//      gösterilmez — seviye şablonun değil, doldurulan kaydın özelliğidir;
//      DÖF ayrı bir şablon türü değil, denetim formunun alt seviyesidir.
//   2) Serbest form (free-form) şablonları → /free-form/templates
// ─────────────────────────────────────────────────────────────────────────────

const KIND_META = {
  inspection: { get label() { return i18n.t("formlar:formsList.denetimFormu"); }, color: "primary" },
  freeform:   { get label() { return i18n.t("formlar:formsList.serbestForm"); },  color: "info"    },
};

const ENTITY_LABELS = {
  get FAIR() { return i18n.t("formlar:formsList.fuar"); },
  get SALON() { return i18n.t("formlar:formsList.salon"); },
  get STAND() { return i18n.t("formlar:formsList.stand"); },
  get PERSON() { return i18n.t("formlar:formsList.kisi"); },
};

const STATUS_LABELS = {
  yayinda: { get label() { return i18n.t("formlar:formsList.yayinda"); }, color: "success"   },
  taslak:  { get label() { return i18n.t("formlar:formsList.taslak"); },  color: "secondary" },
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(currentLocaleTag(), { day: "2-digit", month: "short", year: "numeric" });
}

// ─────────────────────────────────────────────────────────────────────────────
function FormsListPage() {
  const { t: tx } = useTranslation("formlar");
  const navigate = useNavigate();

  const [rows,        setRows]        = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);

  const [search,     setSearch]     = useState("");
  const [kindFilter, setKindFilter] = useState("all");   // all | inspection | freeform
  const [deptFilter, setDeptFilter] = useState("all");   // all | <departmentId>
  const [deleteModal, setDeleteModal] = useState({ open: false, form: null });

  // ── Yükle: birimler + kapsamlar + denetim tipleri + serbest form şablonları ──
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [depts, scopes, inspectionForms, ffRes] = await Promise.all([
        getDepartments().catch(() => []),
        getScopes().catch(() => []),
        formService.getAll().catch(() => []),
        listTemplates({ page: 0, limit: 200 }).catch(() => null),
      ]);

      setDepartments(depts || []);

      const deptNames = {};
      (depts || []).forEach((d) => {
        deptNames[d.inspection_department_id] = d.inspection_department_name;
      });

      // scopeId → { ad, birimId }
      const scopeInfo = {};
      (scopes || []).forEach((s) => {
        scopeInfo[s.inspection_scope_id] = {
          name:   s.translated_name || s.inspection_scope_name,
          deptId: s.inspection_department_id ?? null,
        };
      });

      const inspectionRows = (inspectionForms || []).map((f) => {
        const info = scopeInfo[f.scopeId] || {};
        return {
          key:        `insp-${f.id}`,
          kind:       "inspection",
          id:         f.id,
          name:       f.name,
          shortName:  f.shortName || "",
          deptId:     info.deptId ?? null,
          deptName:   info.deptId != null ? (deptNames[info.deptId] || `Birim #${info.deptId}`) : "—",
          scopeName:  info.name || f.scope || "—",
          status:     f.status || "yayinda",
          isStarred:  !!f.isStarred,
          updatedAt:  f.updatedAt || null,
        };
      });

      const ffList = Array.isArray(ffRes)
        ? ffRes
        : (ffRes?.records || ffRes?.content || ffRes?.templates || []);

      const freeformRows = (ffList || []).map((t) => {
        const id = t._id || t.id || t.templateId;
        return {
          key:             `ff-${id}`,
          kind:            "freeform",
          id,
          name:            t.name || i18n.t("formlar:formsList.sablon", { id }),
          shortName:       "",
          deptId:          null,
          deptName:        "—",
          scopeName:       ENTITY_LABELS[t.targetEntityType] || t.targetEntityType || "—",
          status:          null,
          isStarred:       false,
          updatedAt:       t.updatedAt || t.createdAt || null,
          submissionCount: t.submissionCount ?? 0,
        };
      });

      setRows([...inspectionRows, ...freeformRows]);
    } catch (err) {
      console.error("[FormsListPage] reload:", err);
      setError(i18n.t("formlar:formsList.sablonlarYuklenemedi"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = i18n.t("formlar:formsList.formListesiFds");
    async function run() { await reload(); }
    run();
  }, [reload]);

  // ── Filtrele ──────────────────────────────────────────────────────────────
  const filtered = rows.filter((r) => {
    if (kindFilter !== "all" && r.kind !== kindFilter) return false;
    if (deptFilter !== "all" && String(r.deptId ?? "") !== deptFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      const haystack = `${r.name} ${r.shortName} ${r.deptName} ${r.scopeName}`.toLowerCase();
      if (!haystack.includes(s)) return false;
    }
    return true;
  });

  // Denetim formları önce (yıldızlılar en üstte), sonra serbest formlar;
  // her grup birim → kapsam → ad sırasında.
  const sorted = [...filtered].sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === "inspection" ? -1 : 1;
    if (a.isStarred !== b.isStarred) return a.isStarred ? -1 : 1;
    if (a.deptName !== b.deptName) return a.deptName.localeCompare(b.deptName, "tr");
    if (a.scopeName !== b.scopeName) return a.scopeName.localeCompare(b.scopeName, "tr");
    return String(a.name).localeCompare(String(b.name), "tr");
  });

  const inspectionCount = rows.filter((r) => r.kind === "inspection").length;
  const freeformCount   = rows.filter((r) => r.kind === "freeform").length;

  // ── Aksiyonlar ────────────────────────────────────────────────────────────
  function handleStar(row) {
    if (row.kind !== "inspection") return;
    formService.toggleStar(row.id).then(reload);
  }

  function handleToggleStatus(row) {
    if (row.kind !== "inspection") return;
    formService.toggleStatus(row.id).then(reload);
  }

  function handleEdit(row) {
    if (row.kind === "inspection") navigate(`/Management/Forms/${row.id}/edit`);
    else navigate("/Management/FreeForm?tab=sablonlar");
  }

  function handlePreview(row) {
    if (row.kind === "inspection") navigate(`/Management/Forms/${row.id}/preview`);
    else navigate(`/FdsSaha/FreeForm/${row.id}`);
  }

  function handleDeleteConfirm() {
    const row = deleteModal.form;
    if (!row) return;
    formService.remove(row.id).then(() => {
      setDeleteModal({ open: false, form: null });
      reload();
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("formsList.formListesi")} pageTitle={tx("formsList.formYonetimi")} />

        {/* ── Toolbar ── */}
        <Row className="mb-3 g-2 align-items-center">
          <Col xs={12} sm={4} md={3}>
            <InputGroup size="sm">
              <span className="input-group-text bg-white">
                <Search size={14} className="text-muted" />
              </span>
              <Input
                placeholder={tx("formsList.sablonAdiBirimKapsamAra")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>
          </Col>

          <Col xs={6} sm={3} md={2}>
            <Input
              type="select"
              bsSize="sm"
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
            >
              <option value="all">{tx("formsList.tumSablonlar")}</option>
              <option value="inspection">{tx("formsList.denetimFormu")}</option>
              <option value="freeform">{tx("formsList.serbestForm")}</option>
            </Input>
          </Col>

          <Col xs={6} sm={3} md={3}>
            <Input
              type="select"
              bsSize="sm"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
            >
              <option value="all">{tx("formsList.tumBirimler")}</option>
              {departments.map((d) => (
                <option key={d.inspection_department_id} value={String(d.inspection_department_id)}>
                  {d.inspection_department_name}
                </option>
              ))}
            </Input>
          </Col>

          <Col className="ms-auto text-end d-flex justify-content-end gap-2">
            <Button color="light" size="sm" onClick={reload} disabled={loading}>
              {loading ? <Spinner size="sm" /> : <RefreshCw size={14} />}
            </Button>
            <Button
              color="primary"
              size="sm"
              className="d-inline-flex align-items-center gap-1"
              onClick={() => navigate("/Management/Forms/new")}
            >
              <Plus size={14} />
              {tx("formsList.yeniForm")}
            </Button>
          </Col>
        </Row>

        {error && <div className="alert alert-danger py-2 small">{error}</div>}

        {/* ── Tablo ── */}
        <Card>
          <CardBody className="p-0">
            <div className="table-responsive">
              <Table className="table-hover align-middle mb-0" bordered={false}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 36 }} className="text-center">★</th>
                    <th>{tx("formsList.formAdi")}</th>
                    <th style={{ width: 130 }}>{tx("formsList.tur")}</th>
                    <th style={{ width: 170 }}>{tx("formsList.birim")}</th>
                    <th style={{ width: 120 }}>{tx("formsList.kapsam")}</th>
                    <th style={{ width: 110 }} className="text-center">{tx("formsList.durum")}</th>
                    <th style={{ width: 130 }}>{tx("formsList.sonGuncelleme")}</th>
                    <th style={{ width: 120 }} className="text-center">{tx("formsList.islemler")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="text-center py-5">
                        <Spinner size="sm" className="me-2" />{tx("formsList.yukleniyor")}
                      </td>
                    </tr>
                  ) : sorted.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-5">
                        {rows.length === 0
                          ? tx("formsList.henuzSablonOlusturulmamis")
                          : tx("formsList.aramaKriterlerineUygunSablonBulunamadi")}
                      </td>
                    </tr>
                  ) : (
                    sorted.map((row) => {
                      const kindMeta   = KIND_META[row.kind];
                      const statusMeta = row.status ? (STATUS_LABELS[row.status] || STATUS_LABELS.taslak) : null;
                      return (
                        <tr key={row.key}>
                          {/* Yıldız */}
                          <td className="text-center">
                            {row.kind === "inspection" ? (
                              <button
                                type="button"
                                className="btn btn-link p-0 text-decoration-none"
                                onClick={() => handleStar(row)}
                                title={row.isStarred ? tx("formsList.yildiziKaldir") : tx("formsList.yildizla")}
                              >
                                <Star
                                  size={15}
                                  fill={row.isStarred ? "#f6c23e" : "none"}
                                  stroke={row.isStarred ? "#f6c23e" : "#adb5bd"}
                                />
                              </button>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>

                          {/* Ad + shortName */}
                          <td>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <span
                                className="fw-semibold text-primary"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleEdit(row)}
                              >
                                {row.name}
                              </span>
                              {row.shortName && (
                                <Badge color="light" className="text-dark border" style={{ fontSize: 10 }}>
                                  {row.shortName}
                                </Badge>
                              )}
                              {row.kind === "freeform" && row.submissionCount > 0 && (
                                <Badge color="light" className="text-dark border" style={{ fontSize: 10 }}>
                                  {tx("formsList.kayit", { submissionCount: row.submissionCount })}
                                </Badge>
                              )}
                            </div>
                          </td>

                          {/* Tür */}
                          <td>
                            <Badge color={kindMeta.color} pill className="px-2">
                              {kindMeta.label}
                            </Badge>
                          </td>

                          {/* Birim */}
                          <td><small>{row.deptName}</small></td>

                          {/* Kapsam */}
                          <td><small>{row.scopeName}</small></td>

                          {/* Durum */}
                          <td className="text-center">
                            {statusMeta ? (
                              <Badge
                                color={statusMeta.color}
                                pill
                                className="px-2"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleToggleStatus(row)}
                                title={tx("formsList.durumuDegistir")}
                              >
                                {statusMeta.label}
                              </Badge>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>

                          {/* Tarih */}
                          <td><small className="text-muted">{formatDate(row.updatedAt)}</small></td>

                          {/* İşlemler */}
                          <td>
                            <div className="d-flex justify-content-center gap-1">
                              {/* Düzenle */}
                              <button
                                type="button"
                                id={`edit-${row.key}`}
                                className="btn btn-sm btn-soft-primary p-1"
                                onClick={() => handleEdit(row)}
                              >
                                <Edit2 size={13} />
                              </button>
                              <UncontrolledTooltip target={`edit-${row.key}`}>
                                {row.kind === "inspection" ? tx("common:duzenle") : tx("formsList.serbestFormlarSablonlarDaDuzenle")}
                              </UncontrolledTooltip>

                              {/* Önizle */}
                              <button
                                type="button"
                                id={`preview-${row.key}`}
                                className="btn btn-sm btn-soft-info p-1"
                                onClick={() => handlePreview(row)}
                              >
                                <Eye size={13} />
                              </button>
                              <UncontrolledTooltip target={`preview-${row.key}`}>{tx("formsList.onizle")}</UncontrolledTooltip>

                              {/* Sil — sadece denetim şablonları */}
                              {row.kind === "inspection" && (
                                <>
                                  <button
                                    type="button"
                                    id={`del-${row.key}`}
                                    className="btn btn-sm btn-soft-danger p-1"
                                    onClick={() => setDeleteModal({ open: true, form: row })}
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                  <UncontrolledTooltip target={`del-${row.key}`}>{tx("common:sil")}</UncontrolledTooltip>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>

            {/* Footer */}
            {!loading && rows.length > 0 && (
              <div className="px-3 py-2 border-top d-flex align-items-center text-muted" style={{ fontSize: 12 }}>
                <span>{sorted.length}{" "}{tx("formsList.sablonGosteriliyor")}</span>
                <span className="ms-1">
                  {tx("formsList.toplamDenetimFormuSerbestForm", { length: rows.length, inspectionCount, freeformCount })}
                </span>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>

      {/* ── Silme onay modal'ı ── */}
      <Modal isOpen={deleteModal.open} toggle={() => setDeleteModal({ open: false, form: null })} centered size="sm">
        <ModalHeader toggle={() => setDeleteModal({ open: false, form: null })}>
          {tx("formsList.formuSil")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-1">
            <strong>{deleteModal.form?.name}</strong>{" "}{tx("formsList.formunuSilmekIstediginizeEminMisiniz")}
          </p>
          <small className="text-muted">{tx("formsList.buIslemGeriAlinamaz")}</small>
        </ModalBody>
        <ModalFooter>
          <Button color="light" size="sm" onClick={() => setDeleteModal({ open: false, form: null })}>
            {tx("common:iptal")}
          </Button>
          <Button color="danger" size="sm" onClick={handleDeleteConfirm}>
            {tx("common:sil")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default FormsListPage;
